import { CurrentKey, RelationshipType } from '@/helpers/const';
import { formatAddress } from '@/helpers/format';
import { includesEnum } from '@/helpers/includes-enum';
import {
  initRequestIncomeExpenseMachine,
  loanRequestMachine,
  type RequestIncomeExpenseMachineContext,
} from '@/helpers/loanRequestIncomeExpenseMachine';
import type {
  Address,
  Applicant,
  ApplicantId,
  LoanApplication,
  PropertyId,
  RequestIncomeExpenseProcessStepEnum,
  RequestIncomeExpenseProcessStepStatus,
} from '@/types';
import {
  RequestIncomeExpenseProcessStep,
  RequestIncomeExpenseProcessStepNames,
  RequestIncomeExpenseProcessSteps,
  StepStatus,
} from '@/types';
import { useActor } from '@xstate/vue';
import { computed } from 'vue';
import { interpret } from 'xstate';

const appKey = localStorage.getItem(CurrentKey) ?? '';
const savedState = JSON.parse(localStorage.getItem(appKey) as string);
const initialState: RequestIncomeExpenseMachineContext = savedState ? savedState : initRequestIncomeExpenseMachine();

const service = interpret(loanRequestMachine.withContext(initialState)).start();

service.onTransition((state) => {
  if (state.value !== 'gotoReady' && state.value !== 'start') {
    const currentStep = state.value as RequestIncomeExpenseProcessStepEnum;
    if (state.context.status[currentStep] === StepStatus.Incomplete) {
      state.context.status[currentStep] = StepStatus.Ready;
    }

    state.context.current = currentStep;
    const loanApp = state.context.loanApp;
    if (loanApp.applicationId && loanApp.userType) {
      const key = `${loanApp.actionType}-${loanApp.applicationId}`;
      const appState = JSON.stringify(state.context);
      localStorage.setItem(CurrentKey, key);
      localStorage.setItem(key, appState);
    }
  }
});

const processSteps = Object.entries(RequestIncomeExpenseProcessSteps).map(([step, info]) => ({
  id: step as RequestIncomeExpenseProcessStepEnum,
  ...info,
}));

export function useRequestIncomeExpenseProcess() {
  const { state, send } = useActor(service);
  const currentStep = computed(() => state.value.context.current);
  const loanApp = computed(() => state.value.context.loanApp);
  const authorisedUser = state.value.context.authorisedUser;

  const secondaryApplicant = computed(() => {
    return loanApp.value.actionType === 'updateExisting'
      ? loanApp.value.applicants.find((a) => a.id === state.value.context.authorisedUser.id && a.type === 'secondary')
      : undefined;
  });

  const isSecondaryMarried = computed(
    () => secondaryApplicant.value?.relationshipToPrimary === RelationshipType.MARRIED_OR_DEFACTO,
  );

  const stepper = computed<RequestIncomeExpenseProcessStepStatus[]>(() => {
    const currentStep = RequestIncomeExpenseProcessStepNames.includes(state.value.context.current)
      ? state.value.context.current
      : RequestIncomeExpenseProcessStepNames[0];
    return processSteps
      .filter(
        (step) =>
          loanApp.value.userType &&
          step.actions.includes(loanApp.value.actionType) &&
          step.id !== RequestIncomeExpenseProcessStep.Finished &&
          ((loanApp.value.endDebt > 0 && !isSecondaryMarried.value) ||
            state.value.context.from === 'crm' ||
            !includesEnum(step.id, [
              RequestIncomeExpenseProcessStep.Income,
              RequestIncomeExpenseProcessStep.Expenses,
              RequestIncomeExpenseProcessStep.Assets,
            ]) ||
            // For Secondary request
            (loanApp.value.userType === 'Secondary' &&
              !authorisedUser.id &&
              loanApp.value.actionType === 'updateExisting') ||
            (loanApp.value.endDebt <= 0 &&
              !includesEnum(step.id, [
                RequestIncomeExpenseProcessStep.Income,
                RequestIncomeExpenseProcessStep.Expenses,
                RequestIncomeExpenseProcessStep.Assets,
              ]))),
      )
      .map((step) => {
        const stepStatus = state.value.context.status[step.id];
        const status =
          stepStatus === StepStatus.Incomplete &&
          RequestIncomeExpenseProcessSteps[step.id].order < RequestIncomeExpenseProcessSteps[currentStep].order
            ? StepStatus.Skipped
            : stepStatus;
        return { ...step, status };
      })
      .filter((step) => step.status !== StepStatus.Skipped);
  });

  const loanProperties = computed(() =>
    [...loanApp.value.incomingProperties, ...loanApp.value.outgoingProperties].reduce((obj, item) => {
      obj[item.id] = formatAddress(item.address as Address);
      return obj;
    }, {} as Record<PropertyId, string>),
  );
  const loanApplicants = computed<Record<string, Applicant>>(() =>
    loanApp.value.applicants.reduce((obj, item) => {
      obj[item.id as string] = item;
      return obj;
    }, {} as Record<ApplicantId, Applicant>),
  );

  const saveAndGoNext = (updatedData: Partial<LoanApplication> = {}) => {
    if (currentStep.value === RequestIncomeExpenseProcessStep.SubmitApplication) {
      localStorage.setItem(CurrentKey, '');
    }
    send({ type: 'NEXT', loanApp: { ...state.value.context.loanApp, ...updatedData } });
  };

  const goBack = () => {
    send('BACK');
  };

  const restart = () => {
    send('RESTART');
  };

  return {
    state,
    service,
    stepper,
    currentStep, // remove?
    authorisedUser,
    loanApp,
    // targetApplicant: state.value.context.targetApplicant,
    loanProperties,
    loanApplicants,
    send,
    saveAndGoNext,
    goBack,
    restart,
  };
}
